import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { createIntegration, updateIntegration, deleteIntegration, getIntegrations } from '../api';
import { IntegrationForm } from './IntegrationForm';

export const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [integrations, setIntegrations] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingIntegration, setEditingIntegration] = React.useState<any>(null);

  const integrationTypeLabels: Record<string, string> = React.useMemo(
    () => ({
      bitrix24: 'Bitrix24',
      amocrm: 'amoCRM',
      '1c': '1C',
      email: 'Email',
      emal: 'Email (emal)',
    }),
    [],
  );

  const integrationTypeColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'info' | 'warning' | 'error'> =
    React.useMemo(
      () => ({
        bitrix24: 'primary',
        amocrm: 'secondary',
        '1c': 'info',
        email: 'success',
        emal: 'success',
      }),
      [],
    );

  const fetchIntegrations = React.useCallback(() => {
    setLoading(true);
    getIntegrations()
      .then(data => { setIntegrations(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  React.useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleCreate = async (data: any) => {
    try {
      await createIntegration(data);
      setDialogOpen(false);
      fetchIntegrations();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEdit = async (data: any) => {
    try {
      await updateIntegration(editingIntegration.id, data);
      setDialogOpen(false);
      setEditingIntegration(null);
      fetchIntegrations();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm(t('confirm_delete') || 'Are you sure?')) {
      try {
        await deleteIntegration(id);
        fetchIntegrations();
      } catch (err: any) {
        setError(err.message);
      }
    }
  };

  const handleToggleActive = async (integration: any) => {
    try {
      await updateIntegration(integration.id, { is_active: !integration.is_active });
      fetchIntegrations();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const renderConfigPreview = React.useCallback((config: any) => {
    if (!config || (typeof config === 'object' && Object.keys(config).length === 0)) {
      return '—';
    }

    const configString = typeof config === 'string' ? config : JSON.stringify(config, null, 2);
    const preview = configString.length > 140 ? `${configString.slice(0, 140)}...` : configString;

    return (
      <Tooltip
        title={
          <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', maxWidth: 480 }}>
            {configString}
          </Box>
        }
        placement="top-start"
        arrow
      >
        <Typography
          variant="body2"
          component="pre"
          sx={{
            m: 0,
            whiteSpace: 'pre-wrap',
            maxHeight: 96,
            overflow: 'hidden',
            fontFamily: 'monospace',
          }}
        >
          {preview}
        </Typography>
      </Tooltip>
    );
  }, []);

  const getIntegrationField = React.useCallback((integration: any, keys: string[]) => {
    for (const key of keys) {
      const value =
        integration[key] ??
        (integration.config && typeof integration.config === 'object'
          ? integration.config[key]
          : undefined);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }
    return null;
  }, []);

  return (
    <>
      <Box mb={2} display="flex" gap={2} alignItems="center">
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => setDialogOpen(true)}
          fullWidth={isMobile}
        >
          {t('add_integration') || 'Добавить интеграцию'}
        </Button>
      </Box>
      {loading ? (
        <Typography>{t('loading') || 'Loading...'}</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {integrations.map((integration) => (
            <Card key={integration.id}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    {integration.project_name || 'N/A'}
                  </Typography>
                  <Chip 
                    label={integrationTypeLabels[integration.integration_type] || integration.integration_type.toUpperCase()} 
                    size="small"
                    color={integrationTypeColors[integration.integration_type] || 'default'}
                  />
                </Box>
                
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  ID: {integration.id} | Ext ID: {integration.external_project_id || 'N/A'}
                </Typography>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">URL / Webhook:</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {getIntegrationField(integration, ['webhook_url', 'url', 'base_url']) || '—'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Source ID:</Typography>
                    <Typography variant="body2">{getIntegrationField(integration, ['source_id']) || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">Assigned By:</Typography>
                    <Typography variant="body2">
                      {getIntegrationField(integration, ['assigned_by_id', 'responsible_user_id']) || '—'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block">Config Preview:</Typography>
                  <Box sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, mt: 0.5, overflowX: 'auto' }}>
                    {renderConfigPreview(integration.config)}
                  </Box>
                </Box>

                <Divider sx={{ my: 1 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      checked={integration.is_active}
                      onChange={() => handleToggleActive(integration)}
                      size="small"
                    />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      {integration.is_active ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton size="small" onClick={() => { setEditingIntegration(integration); setDialogOpen(true); }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(integration.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
          {!integrations.length && (
            <Typography align="center" color="text.secondary" sx={{ py: 2 }}>
              Интеграции не найдены
            </Typography>
          )}
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Webhook URL</TableCell>
                <TableCell>Source ID</TableCell>
                <TableCell>Assigned By ID</TableCell>
                <TableCell>Config</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {integrations.map((integration) => (
                <TableRow key={integration.id}>
                  <TableCell>{integration.id}</TableCell>
                  <TableCell>
                    {integration.project_name || 'N/A'}
                    <br />
                    <small style={{ color: '#666' }}>
                      External ID: {integration.external_project_id || 'N/A'}
                    </small>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={integrationTypeLabels[integration.integration_type] || integration.integration_type.toUpperCase()} 
                      size="small"
                      color={integrationTypeColors[integration.integration_type] || 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {getIntegrationField(integration, ['webhook_url', 'url', 'base_url']) || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getIntegrationField(integration, ['source_id']) || '—'}</TableCell>
                  <TableCell>
                    {getIntegrationField(integration, ['assigned_by_id', 'responsible_user_id']) || '—'}
                  </TableCell>
                  <TableCell>{renderConfigPreview(integration.config)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={integration.is_active}
                      onChange={() => handleToggleActive(integration)}
                      size="small"
                    />
                    <Chip
                      label={integration.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={integration.is_active ? 'success' : 'default'}
                      style={{ marginLeft: '8px' }}
                    />
                  </TableCell>
                  <TableCell>{new Date(integration.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => { setEditingIntegration(integration); setDialogOpen(true); }}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(integration.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <IntegrationForm
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); setEditingIntegration(null); }}
        integration={editingIntegration}
        onSubmit={editingIntegration ? handleEdit : handleCreate}
      />
    </>
  );
};

